/**
 * 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE" · Silent Error Reporter.
 *
 * Batcher en memoria para errores del Iframe de Preview capturados en el
 * Área de Trabajo. Los eventos se acumulan y se envían al backend cada 10s
 * en batches de hasta 10 errores. Nunca interrumpe el UX: si la red falla,
 * el batch se descarta silenciosamente.
 *
 * Uso:
 *   import { reportSilentError } from '../utils/silentErrorReporter';
 *   reportSilentError({
 *     type: 'runtime',
 *     error: 'SomeError: ...',
 *     module_id: 'mod_xyz',
 *     component: 'UnifiedCodePreview',
 *     workspace_id: 'ws_123',
 *     timestamp: Date.now(),
 *     extra: { snippet: '...' },
 *   });
 *
 * Configuración:
 *   FLUSH_INTERVAL_MS  = 10_000  (10s)
 *   MAX_BATCH_SIZE     = 10
 *   MAX_QUEUE_SIZE     = 100 (drops silenciosos si se excede)
 */

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH_SIZE = 10;
const MAX_QUEUE_SIZE = 100;

let queue = [];
let timerId = null;
let inflight = false;

/** Resuelve la base URL del backend desde el entorno (CRA). */
function getBackendBase() {
  const env = (typeof process !== 'undefined' && process.env) || {};
  const url = env.REACT_APP_BACKEND_URL;
  if (typeof url === 'string' && url.length > 0) return url.replace(/\/$/, '');
  return '';
}

/** Sanea un evento antes de encolarlo (defensa contra objetos raros). */
function sanitize(event) {
  if (!event || typeof event !== 'object') return null;
  const safe = {
    type: String(event.type || 'unknown').slice(0, 64),
    error: event.error != null ? String(event.error).slice(0, 4000) : undefined,
    message: event.message != null ? String(event.message).slice(0, 4000) : undefined,
    timestamp: Number.isFinite(event.timestamp) ? Math.floor(event.timestamp) : Date.now(),
    module_id: event.module_id ? String(event.module_id).slice(0, 128) : undefined,
    component: event.component ? String(event.component).slice(0, 128) : undefined,
    workspace_id: event.workspace_id ? String(event.workspace_id).slice(0, 128) : undefined,
    extra: undefined,
  };
  if (event.extra && typeof event.extra === 'object') {
    try {
      // Recorte defensivo: máx 20 llaves, valores string a 4KB.
      const trimmed = {};
      const keys = Object.keys(event.extra).slice(0, 20);
      for (const k of keys) {
        const v = event.extra[k];
        if (v == null) continue;
        if (typeof v === 'string') trimmed[k] = v.slice(0, 4000);
        else if (typeof v === 'number' || typeof v === 'boolean') trimmed[k] = v;
        else {
          try { trimmed[k] = JSON.stringify(v).slice(0, 4000); } catch (_) { /* skip */ }
        }
      }
      safe.extra = trimmed;
    } catch (_) { /* ignore */ }
  }
  return safe;
}

/** Añade un evento a la cola. Nunca lanza. */
export function reportSilentError(event) {
  try {
    const clean = sanitize(event);
    if (!clean) return;
    if (queue.length >= MAX_QUEUE_SIZE) return; // drop silencioso
    queue.push(clean);
    ensureTimer();
  } catch (_) { /* silent */ }
}

/** Fuerza un flush inmediato. Útil para tests o beforeunload. */
export async function flushSilentErrors() {
  if (inflight) return;
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH_SIZE);
  const base = getBackendBase();
  if (!base) {
    // Sin URL: soltamos el batch para evitar acumulación.
    return;
  }
  inflight = true;
  try {
    // Usamos `keepalive` para que el request sobreviva a un unload rápido.
    // No adjuntamos Authorization Bearer: usamos la cookie httpOnly con
    // credentials: 'include'. Si el user no está logueado, va como anon.
    await fetch(`${base}/api/telemetry/preview-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      credentials: 'include',
      keepalive: true,
    });
  } catch (_) {
    // Silencio total. La telemetría NUNCA rompe el UX.
  } finally {
    inflight = false;
  }
  // Si aún quedan eventos, mantener el timer vivo.
  if (queue.length > 0) ensureTimer();
}

function ensureTimer() {
  if (timerId != null) return;
  if (typeof window === 'undefined') return;
  timerId = window.setInterval(() => {
    // Fire and forget.
    flushSilentErrors().catch(() => {});
  }, FLUSH_INTERVAL_MS);
}

/** Detiene el timer. Solo para tests y hot-reload. */
export function stopSilentErrorReporter() {
  if (timerId != null && typeof window !== 'undefined') {
    window.clearInterval(timerId);
    timerId = null;
  }
}

/** Introspección para tests. */
export function _getQueueSnapshot() {
  return queue.slice();
}

/** Limpia la cola. Solo para tests. */
export function _resetQueueForTests() {
  queue = [];
  stopSilentErrorReporter();
}

// Flush oportunista al cerrar la pestaña.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // No await — beforeunload es síncrono; el keepalive de fetch se encarga.
    try { flushSilentErrors(); } catch (_) { /* silent */ }
  });
}
